/**
 * MAM Component — View model, enums, and mapper (MVP).
 * Maps from MediaItem (domain/media); does not replace or modify it.
 */

import moment from "moment";
import { ContentUploadStatus, MediaItem, MediaType } from "../media";

// ── Enums (MAM UI layer) ───────────────────────────────────

export enum MamStatus {
  Ready = "Ready",
  Uploading = "Uploading",
  Deleted = "Deleted",
}

export enum MamSource {
  Ready = "Ready",
  Upload = "Uploading",
  Recorded = "Recorded",
}

export enum MamType {
  Video = "Video",
  Audio = "Audio",
}

// ── View model (one row in the asset table) ─────────────────

export interface MamAsset {
  id: number;
  videoId: string;
  title: string;
  thumbnailUrl: string | null;
  durationFormatted: string;
  type: MamType;
  status: MamStatus;
  source: MamSource;
  createdAt: string | null;
}

// ── Filters ─────────────────────────────────────────────────

/**
 * True for MAM list items that are playable video
 * (API ContentMediaType AudioVideo / Video).
 */
export function isVideoMediaItem(
  contentMediaType: MediaType | number | string | undefined | null,
): boolean {
  if (contentMediaType === undefined || contentMediaType === null) {
    return false;
  }
  if (typeof contentMediaType === "string") {
    return (
      contentMediaType === "AudioVideo" || contentMediaType === "Video"
    );
  }
  const t = Number(contentMediaType);
  return t === MediaType.AudioVideo || t === MediaType.Video;
}

// ── Mapper ──────────────────────────────────────────────────

function mapMediaType(mediaType: MediaType | undefined): MamType {
  switch (mediaType) {
    case MediaType.Audio:
      return MamType.Audio;
    case MediaType.Video:
    case MediaType.AudioVideo:
    default:
      return MamType.Video;
  }
}

function mapUploadStatus(status: ContentUploadStatus | undefined): MamStatus {
  switch (status) {
    case ContentUploadStatus.Enabled:
      return MamStatus.Ready;
    case ContentUploadStatus.Pending:
      return MamStatus.Uploading;
    case ContentUploadStatus.Disabled:
      return MamStatus.Deleted;
    default:
      return MamStatus.Ready;
  }
}

function formatDuration(duration: moment.Duration | undefined): string {
  if (!duration) {
    return "00:00:00";
  }
  return moment.utc(duration.asMilliseconds()).format("HH:mm:ss");
}

function formatCreatedAt(uploadedTimeMs: number | undefined): string | null {
  if (uploadedTimeMs == null || Number.isNaN(uploadedTimeMs)) {
    return null;
  }
  return moment(uploadedTimeMs).format("YYYY-MM-DD HH:mm");
}

/**
 * Maps a raw MediaItem (from getMedia) to the MAM view-model shape.
 *
 * Notes:
 * - `createdAt` comes from list API `UploadedTime` via `MediaItem.uploadedTimeMs`.
 * - `videoId` prefers `uid`; falls back to `tenantMediaId` then empty string.
 * - `type` defaults to Video for unknown / unmapped media types.
 */
export function mapMediaItemToMamAsset(item: MediaItem): MamAsset {
  return {
    id: item.id ?? 0,
    videoId: item.uid || item.tenantMediaId || "",
    title: item.name || item.userFilename || "",
    thumbnailUrl: item.contentSnapshotUrl ?? null,
    durationFormatted: formatDuration(item.contentDuration),
    type: mapMediaType(item.contentMediaType),
    status: mapUploadStatus(item.contentUploadStatus),
    source: item.recording === true ? MamSource.Recorded : MamSource.Upload,
    createdAt: formatCreatedAt(item.uploadedTimeMs),
  };
}
